import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Copy, Eye, Loader2, Mail, Plus, RefreshCw, Trash2, Zap } from "lucide-react";

const KIND_LABEL = { preis: "Preis", aktion: "Aktion", sortiment: "Sortiment", service: "Service", event: "Event", news: "News", newsletter: "Newsletter", preisalarm: "PREISALARM" };
const KIND_COLOR = { preis: "bg-rose-100 text-rose-800", aktion: "bg-amber-100 text-amber-800", sortiment: "bg-sky-100 text-sky-800", service: "bg-emerald-100 text-emerald-800", event: "bg-violet-100 text-violet-800", news: "bg-gray-100 text-gray-700", newsletter: "bg-indigo-100 text-indigo-800", preisalarm: "bg-red-600 text-white" };

function SourcesCard({ overview, onChanged }) {
    const [domain, setDomain] = useState("");
    const [busy, setBusy] = useState(false);
    const { sources = [], free_limit = 5, allowed_domains = 5, pricing = {} } = overview || {};
    const add = async () => {
        if (!domain.trim()) return;
        setBusy(true);
        try {
            await api.post("/dealer/competitors/sources", { domain });
            setDomain("");
            toast.success("Mitbewerber-Domain hinzugefügt — Beobachtung startet automatisch.");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Hinzufügen"); }
        setBusy(false);
    };
    const remove = async (s) => {
        try { await api.delete(`/dealer/competitors/sources/${s.id}`); toast.success("Domain entfernt."); onChanged(); }
        catch { toast.error("Fehler beim Entfernen"); }
    };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="comp-sources-card">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-black">Beobachtete Mitbewerber</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 rounded-sm px-2 py-1" data-testid="comp-limit-badge">
                    {sources.length} / {allowed_domains} Domains
                </span>
            </div>
            <div className="flex gap-2 mb-3">
                <Input value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder="mitbewerber.de" className="rounded-sm flex-1" data-testid="comp-domain-input" />
                <Button type="button" onClick={add} disabled={busy} className="h-10 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold shrink-0" data-testid="comp-domain-add">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
            </div>
            <div className="divide-y divide-gray-100" data-testid="comp-sources-list">
                {sources.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 py-2" data-testid={`comp-source-${s.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold truncate">{s.domain}</div>
                            <div className="text-[10px] text-gray-400">
                                {s.last_crawled_at ? `Zuletzt: ${new Date(s.last_crawled_at).toLocaleString("de-DE")} · ${s.last_status || ""}` : "Noch nicht gecrawlt"}
                            </div>
                        </div>
                        <button type="button" onClick={() => remove(s)} className="text-red-500 hover:text-red-700" data-testid={`comp-source-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
                {sources.length === 0 && <div className="text-xs text-gray-400 py-4 text-center" data-testid="comp-sources-empty">Noch keine Mitbewerber — Domain oben eintragen (bis zu {free_limit} kostenlos).</div>}
            </div>
            <div className="mt-3 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-2" data-testid="comp-upsell-hint">
                Bis zu <b>{free_limit} Domains kostenlos</b> — mehr Domains und Social-Media-Beobachtung direkt unten bei <b>Add-ons</b> freischalten, Abrechnung automatisch über „Kosten &amp; Nutzung".
            </div>
        </div>
    );
}

function AddonsCard({ overview, onChanged }) {
    const settings = overview?.settings || {};
    const pricing = overview?.pricing || {};
    const [busy, setBusy] = useState(false);
    const fmtEur = (v, def) => Number(v ?? def).toFixed(2).replace(".", ",");
    const save = async (payload, msg) => {
        setBusy(true);
        try {
            await api.post("/dealer/competitors/addons", payload);
            toast.success(msg);
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Änderung fehlgeschlagen"); }
        setBusy(false);
    };
    const extra = settings.extra_domains || 0;
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="comp-addons-card">
            <h2 className="text-sm font-black mb-1 flex items-center gap-1.5"><Zap className="w-4 h-4" /> Add-ons (Selbstbuchung)</h2>
            <p className="text-xs text-gray-500 mb-3">Sofort aktiv — Abrechnung automatisch und tagesanteilig über „Kosten &amp; Nutzung".</p>
            <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-100">
                <div>
                    <div className="text-sm font-bold">Zusatz-Domains</div>
                    <div className="text-[11px] text-gray-500">{fmtEur(pricing.extra_domain_eur, 5)} € / Monat je Domain · aktuell erlaubt: {overview?.allowed_domains ?? 5}</div>
                </div>
                <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={busy || extra <= 0}
                        onClick={() => save({ extra_domains: extra - 1 }, "Zusatz-Domain entfernt.")}
                        className="h-8 w-8 p-0 rounded-sm" data-testid="comp-addon-domain-minus">−</Button>
                    <span className="w-6 text-center text-sm font-black" data-testid="comp-addon-domain-count">{extra}</span>
                    <Button type="button" size="sm" disabled={busy}
                        onClick={() => save({ extra_domains: extra + 1 }, `Zusatz-Domain freigeschaltet (${fmtEur(pricing.extra_domain_eur, 5)} € / Monat).`)}
                        className="h-8 w-8 p-0 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black" data-testid="comp-addon-domain-plus">+</Button>
                </div>
            </div>
            <div className="flex items-center justify-between gap-2 py-2">
                <div>
                    <div className="text-sm font-bold">Social-Media-Beobachtung</div>
                    <div className="text-[11px] text-gray-500">{fmtEur(pricing.social_eur, 10)} € / Monat — beobachtet auch Social-Profile der Mitbewerber</div>
                </div>
                <Button type="button" size="sm" disabled={busy}
                    onClick={() => save({ social_enabled: !settings.social_enabled },
                        settings.social_enabled ? "Social-Beobachtung deaktiviert." : `Social-Beobachtung aktiviert (${fmtEur(pricing.social_eur, 10)} € / Monat).`)}
                    className={`h-8 rounded-sm text-xs font-bold ${settings.social_enabled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                    data-testid="comp-addon-social-toggle">
                    {settings.social_enabled ? "Aktiv ✓" : "Aktivieren"}
                </Button>
            </div>
        </div>
    );
}

function AliasCard({ overview, onChanged }) {
    const alias = overview?.settings?.alias_email;
    const [busy, setBusy] = useState(false);
    const create = async () => {
        setBusy(true);
        try { await api.post("/dealer/competitors/alias-email"); toast.success("Anonyme Beobachtungs-Adresse erstellt."); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="comp-alias-card">
            <h2 className="text-sm font-black mb-1 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Anonyme Newsletter-Adresse</h2>
            <p className="text-xs text-gray-500 mb-3">Mit dieser Adresse abonnieren Sie anonym die Newsletter Ihrer Mitbewerber — eingehende Mails landen automatisch in Ihrer Beobachtungsliste.</p>
            {alias ? (
                <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-bold bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 truncate" data-testid="comp-alias-email">{alias}</code>
                    <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(alias); toast.success("Adresse kopiert."); }} className="h-9 rounded-sm" data-testid="comp-alias-copy"><Copy className="w-4 h-4" /></Button>
                </div>
            ) : (
                <Button type="button" onClick={create} disabled={busy} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-xs font-bold" data-testid="comp-alias-create">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Adresse erstellen
                </Button>
            )}
        </div>
    );
}

export default function DealerCompetitorsPage() {
    const [overview, setOverview] = useState(null);
    const [topics, setTopics] = useState([]);
    const [crawling, setCrawling] = useState(false);

    const load = useCallback(async () => {
        try {
            const [o, t] = await Promise.all([
                api.get("/dealer/competitors/overview"),
                api.get("/dealer/competitors/topics?limit=100"),
            ]);
            setOverview(o.data);
            setTopics(t.data.items || []);
        } catch { toast.error("Mitbewerber-Daten konnten nicht geladen werden."); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const crawl = async () => {
        setCrawling(true);
        try {
            const { data } = await api.post("/dealer/competitors/crawl");
            const added = (data.results || []).reduce((a, r) => a + (r.topics_added || 0), 0);
            toast.success(`Beobachtung abgeschlossen — ${added} neue Erkenntnis${added !== 1 ? "se" : ""}.`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Crawl fehlgeschlagen"); }
        setCrawling(false);
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="competitors-page">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <h1 className="font-display text-3xl font-black text-[#111827] flex items-center gap-2"><Eye className="w-7 h-7" /> Mitbewerber</h1>
                <Button type="button" onClick={crawl} disabled={crawling || !(overview?.sources || []).length} className="h-9 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-yellow-400 text-xs font-bold" data-testid="comp-crawl-btn">
                    {crawling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Jetzt beobachten
                </Button>
            </div>
            <p className="text-sm text-gray-500 mb-6">Die KI beobachtet die Webseiten Ihrer Mitbewerber und meldet Preisaktionen, Sortiments-Änderungen und Neuigkeiten — automatisch mehrmals täglich.</p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
                <SourcesCard overview={overview} onChanged={load} />
                <div className="space-y-6">
                    <AddonsCard overview={overview} onChanged={load} />
                    <AliasCard overview={overview} onChanged={load} />
                </div>
            </div>
            <h2 className="text-lg font-black mb-2">Beobachtungen ({topics.length})</h2>
            <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100" data-testid="comp-topics-list">
                {topics.map((t) => (
                    <div key={t.id} className="px-3 py-2.5 flex items-start gap-2" data-testid={`comp-topic-${t.id}`}>
                        <span className={`mt-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${KIND_COLOR[t.kind] || KIND_COLOR.news}`}>{KIND_LABEL[t.kind] || t.kind}</span>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold">{t.title}</div>
                            <div className="text-xs text-gray-500">{t.summary}</div>
                            <div className="text-[10px] text-gray-400 mt-0.5">{t.domain} · {new Date(t.created_at).toLocaleString("de-DE")}</div>
                        </div>
                    </div>
                ))}
                {topics.length === 0 && <div className="px-3 py-8 text-sm text-gray-400 text-center" data-testid="comp-topics-empty">Noch keine Beobachtungen — Domains hinzufügen und „Jetzt beobachten" klicken.</div>}
            </div>
        </div>
    );
}
